/* eslint-disable jsx-a11y/tabindex-no-positive, camelcase */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import getUid from '@clearscore/helpers.uid';
import { isAndroidBrowser } from '@clearscore/helpers.device';
import { setCookie, getCookie } from '@clearscore/helpers.cookies';
import { actions as sessionActions } from '@clearscore/redux.session';
import { isRequiredValidation } from '@clearscore/validation.is-required';
import { isEmailValidation } from '@clearscore/validation.is-email';
import normalizeEmail from '@clearscore/normalize.email';
import Text from '@clearscore/rainbow.text';
import Icon from '@clearscore/rainbow.icons.padlock';
import Button from '@clearscore/rainbow.button';
import TextInput from '@clearscore/rainbow.text-input';
import Password from '@clearscore/rainbow.password';
import Checkbox from '@clearscore/rainbow.checkbox';
import Label from '@clearscore/rainbow.label';
import FormLayout from '@clearscore/rainbow.form-layout';

import { COOKIE_ANONYMOUS_ID } from '../../lib/constants';
import { setUserAlreadyRegistered } from '../../lib/redux';
import style from './styles/index.css';

const clearBrowserHistory = (history, locationState = {}) => {
    const { isAlreadyRegistered, registeredEmailValue, ...locationStateRest } = locationState;
    const { searchParams } = new URL(window.location.href);
    if (isAlreadyRegistered || searchParams.has('email')) {
        searchParams.delete('email');
        history.replace({
            state: { ...locationStateRest },
            search: searchParams.toString(),
        });
    }
};

const getAnonymousId = () => {
    let anonymousId = getCookie(COOKIE_ANONYMOUS_ID);

    if (!anonymousId) {
        anonymousId = getUid();
        setCookie(COOKIE_ANONYMOUS_ID, anonymousId);
    }

    return anonymousId;
};

/**
 * Login Form
 * @param props
 * @return {XML}
 * @constructor
 */
function LoginForm({
    language,
    locationState,
    isAlreadyRegisteredPath,
    isAlertOpen,
    history,
    initialValues,
    isLoading,
}) {
    const dispatch = useDispatch();
    const anonymousId = getAnonymousId();

    const [isChecked, setIsChecked] = useState(false);

    useEffect(() => {
        if (isAndroidBrowser()) {
            setIsChecked(true);
        }
    }, []);

    const loginAction = (formData) =>
        dispatch(sessionActions.login({ ...formData, anonymous_id: anonymousId }, { session_id: anonymousId }));

    // N.B. We need to clear browser history state to prevent populating input on page refresh
    useEffect(() => clearBrowserHistory(history, locationState), [history, locationState]);

    useEffect(() => {
        if (isAlertOpen) return;
        if (isAlreadyRegisteredPath || locationState.isAlreadyRegistered) dispatch(setUserAlreadyRegistered());
    }, [dispatch, isAlertOpen, isAlreadyRegisteredPath, locationState]);

    return (
        <Form
            name={LoginForm}
            initialValues={initialValues}
            onSubmit={loginAction}
            render={({ handleSubmit, pristine, invalid }) => (
                <form className={style.formWrapper} onSubmit={handleSubmit}>
                    <Text.H1>{language.mainTitle}</Text.H1>
                    <FormLayout>
                        <FormLayout.Fieldset>
                            <FormLayout.Item>
                                <Label htmlFor="email">{language.email.label}</Label>

                                <Field
                                    name="email"
                                    type="email"
                                    parse={normalizeEmail}
                                    validate={(data) => isRequiredValidation()(data) || isEmailValidation()(data)}
                                >
                                    {({ input, meta }) => (
                                        <TextInput
                                            id="email"
                                            name="email"
                                            type="email"
                                            tabIndex="1"
                                            autoFocus
                                            value={input.value}
                                            placeholder={language.email.placeholder}
                                            onBlur={input.onBlur}
                                            onChange={input.onChange}
                                            isValid={meta.touched && meta.valid}
                                            isInvalid={meta.touched && meta.invalid}
                                            errorMessage={(meta.touched && meta.error) || ''}
                                        />
                                    )}
                                </Field>
                            </FormLayout.Item>

                            <FormLayout.Item>
                                <div className={style.passwordLabelWrapper}>
                                    <Label htmlFor="password">{language.password.label}</Label>

                                    <Text.Link1 dataId="reset-password-anchor-link" href="/reset">
                                        {language.forgotPassword}
                                    </Text.Link1>
                                </div>

                                <Field
                                    name="password"
                                    type="password"
                                    validate={(data) => isRequiredValidation()(data)}
                                >
                                    {({ input, meta }) => (
                                        <Password
                                            id="password"
                                            name="password"
                                            tabIndex="2"
                                            placeholder={language.password.placeholder}
                                            onBlur={input.onBlur}
                                            onChange={input.onChange}
                                            isValid={meta.touched && meta.valid}
                                            isInvalid={meta.touched && meta.invalid}
                                            errorMessage={(meta.touched && meta.error) || ''}
                                            value={input.value}
                                        />
                                    )}
                                </Field>
                            </FormLayout.Item>

                            <FormLayout.Item>
                                <Field name="remember" type="checkbox">
                                    {({ input }) => (
                                        <Checkbox
                                            {...input}
                                            tabIndex="3"
                                            onClick={() => setIsChecked(!isChecked)}
                                            checked={isChecked}
                                        >
                                            {language.rememberMe}
                                        </Checkbox>
                                    )}
                                </Field>
                            </FormLayout.Item>

                            <FormLayout.Item>
                                <Button
                                    htmlType="submit"
                                    tabIndex="4"
                                    dataId="submit"
                                    isResponsive
                                    type={Button.types.PRIMARY}
                                    size={Button.sizes.LARGE}
                                    isLoading={isLoading}
                                    isDisabled={pristine || isLoading || invalid}
                                >
                                    {language.submit.text}
                                </Button>
                            </FormLayout.Item>
                        </FormLayout.Fieldset>
                    </FormLayout>

                    <div className={style.linksWrapper}>
                        <Text.Link1 dataId="cant-login-anchor-link" href="/login/help">
                            {language.cantLogin}
                        </Text.Link1>

                        <Text.Link1 dataId="register-email-anchor-link" href="/signup">
                            {language.newAccount}
                        </Text.Link1>
                    </div>

                    <div className={style.security} data-qa="security-message">
                        <Icon height={16} width={16} />
                        <Text.Body2>{language.securityMessage}</Text.Body2>
                    </div>
                </form>
            )}
        />
    );
}

LoginForm.propTypes = {
    language: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    isAlertOpen: PropTypes.bool.isRequired,
    isAlreadyRegisteredPath: PropTypes.bool.isRequired,
    initialValues: PropTypes.shape({
        email: PropTypes.string.isRequired,
    }).isRequired,
    locationState: PropTypes.shape({
        registeredEmailValue: PropTypes.string,
        isAlreadyRegistered: PropTypes.bool,
    }).isRequired,
    isLoading: PropTypes.bool.isRequired,
};

export default LoginForm;
